import React, { useState, useRef, useEffect } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import instance from "../../../services/baseService";
import { FaFileImport } from "react-icons/fa6";
import "./importFiles.css";

const ImportDrawer = ({ onImport, folder_id, gstrFormName }) => {
  const [show, setShow] = useState(false);
  const [closing, setClosing] = useState(false);
  const [repoUrl, setRepoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const drawerRef = useRef();

  const handleClose = () => setShow(false);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        toggleDrawer();
      }
    };

    if (show) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [show]);

  const toggleDrawer = () => {
    if (show) {
      setClosing(true);
      setTimeout(() => {
        setShow(false);
        setClosing(false);
      }, 300); // Match with CSS transition duration
    } else {
      setRepoUrl('');
      setError('');
      setShow(true);
    }
  };

  const validateUrl = (url) => {
    const urlRegex = /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i;
    return urlRegex.test(url);
  };

  const handleSubmit = async () => {
    if (gstrFormName === "refund" && (!folder_id || folder_id === null)) {
      setError('Select a Period before fetching');
      return;
    }

    if (!repoUrl.trim()) {
      setError('URL is required.');
      return;
    }
    if (!validateUrl(repoUrl)) {
      setError('Please enter a valid URL.');
      return;
    }

    const urlParts = repoUrl.trim().split('/');
    const externalShareId = urlParts[urlParts.length - 1];

    if (!externalShareId) {
      setError('Invalid URL format. Could not extract ID.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const res = await instance.get(`/api/v1/push/fetch/files/${externalShareId}`);

      if (res.data && res.data.type === 'success') {
        onImport(res.data.data);
        handleClose();
      } else {
        setError('Failed to fetch data. Please check the URL and try again.');
      }
    } catch (error) {
      console.error('Error fetching repo data:', error);

      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || 'Server error occurred';
        if (status === 404) {
          setError('Repository not found. Please check the URL.');
        } else if (status === 403) {
          setError('Access denied. You may not have permission to access this repository.');
        } else if (status === 400) {
          setError('Invalid request. Please check the URL format.');
        } else {
          setError(`Error: ${message}`);
        }
      } else if (error.request) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isRefundWithoutFolder = gstrFormName === "refund" && (!folder_id || folder_id === null);
  const isButtonDisabled = loading || isRefundWithoutFolder;

  useEffect(() => {
    if (isRefundWithoutFolder && !error) {
      setError('Select a Period before fetching');
    } else if (!isRefundWithoutFolder && error === 'Select a Period before fetching') {
      setError('');
    }
  }, [isRefundWithoutFolder, error]);

  return (
    <>
      {show ? (
        <div
          ref={drawerRef}
          className={`position-fixed bottom-10 end-0 m-3 p-0 bg-white border rounded shadow-lg import-drawer-modal ${closing ? "closing" : ""}`}
        >
          <div className="import-drawer-header d-flex align-items-center justify-content-between p-2">
            <div className="d-flex align-items-center">
              <div className="avatar rounded-circle d-flex align-items-center justify-content-center me-2"></div>
              <h5 className="mb-0 text-white">Import Shared Repo</h5>
            </div>
            <button
              className="btn btn-link text-white p-0 import-drawer-close-button"
              onClick={toggleDrawer}
            >
              ×
            </button>
          </div>
          <div className="import-drawer-body">
            <Form>
              <Form.Group controlId="formRepoUrl" className="mb-3">
                <Form.Label>Shared Repo URL</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter shared repo URL"
                  value={repoUrl}
                  onChange={(e) => {
                    setRepoUrl(e.target.value);
                    if (error) setError('');
                  }}
                  isInvalid={!!error}
                />
                {error && (
                  <Form.Control.Feedback type="invalid">
                    {error}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isButtonDisabled}
                className="w-100 fw-bold"
              >
                {loading ? <Spinner animation="border" size="sm" /> : 'Fetch Contents'}
              </Button>
            </Form>
          </div>
        </div>
      ) : (
        <div
          className="position-fixed bottom-24 end-0 m-3 p-3 rounded-circle bg-primary d-flex align-items-center justify-content-center messenger-icon"
          onClick={toggleDrawer}
        >
          <FaFileImport />
        </div>
      )}
    </>
  );
};

export default ImportDrawer;
